import React, { useState } from "react";
import {
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Controller,
	type Control,
	type FieldErrors,
	type UseFormRegister,
} from "react-hook-form";
export const PRESET_HALLS = ["CARNIVAL HALL", "HARMONY HALL", "MILKY WAY", "CELEBRITY HALL",];



interface Props {
	fieldId?: string;
	index: number;
	control: Control<any>;
	register: UseFormRegister<any>;
	errors: FieldErrors<any>;
	removeStall: (index: number) => void;
	canDelete: boolean;
	onHallChange?: (newHall: string) => void;
}

const StallItemRow: React.FC<Props> = ({
	fieldId,
	index,
	control,
	register,
	errors,
	removeStall,
	canDelete,
	onHallChange,
}) => {
	const [isOther, setIsOther] = useState<boolean>(false);

	const rowLabelId = fieldId ? `hall-select-label-${fieldId}` : `hall-select-label-${index}`;

	return (
		<Paper
			variant="outlined"
			sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
		>
			<Controller
				control={control}
				name={`stalls.${index}.hall_name` as const}
				rules={{
					required: "Hall name is required",
					validate: (val) =>
						(val &&
							val.trim() !== "" &&
							val.trim().toLowerCase() !== "other") ||
						"Hall name is required",
				}}
				render={({ field: { value, onChange }, fieldState: { error } }) => {
					const isPreset = PRESET_HALLS.includes(value || "");
					const effectiveIsOther = isOther && !isPreset;

					const handleSelectChange = (val: string) => {
						if (val === "other") {
							setIsOther(true);
							onChange("");
							if (index === 0 && onHallChange) onHallChange("");
						} else {
							setIsOther(false);
							onChange(val);
							if (index === 0 && onHallChange) onHallChange(val);
						}
					};

					const selectValue = effectiveIsOther
						? "other"
						: isPreset
							? value
							: "";

					return (
						<Grid container spacing={2} alignItems="center">
							{/* Hall Selection Dropdown */}
							<Grid item key="hall-select-col" xs={12} sm={effectiveIsOther ? 3.5 : 5.5}>
								<FormControl
									fullWidth
									size="small"
									required
									error={!effectiveIsOther && !!error}
								>
									<InputLabel id={rowLabelId}>
										{`Hall Name - ${index + 1}`}
									</InputLabel>
									<Select
										labelId={rowLabelId}
										label={`Hall Name - ${index + 1}`}
										value={selectValue}
										onChange={(e) =>
											handleSelectChange(e.target.value as string)
										}
									>
										{PRESET_HALLS.map((hall) => (
											<MenuItem key={hall} value={hall}>
												{hall}
											</MenuItem>
										))}
										<MenuItem
											key="other"
											value="other"
											sx={{ fontWeight: 600, color: "primary.main" }}
										>
											Other (Type custom)
										</MenuItem>
									</Select>
									{!effectiveIsOther && error && (
										<FormHelperText error>{error.message}</FormHelperText>
									)}
								</FormControl>
							</Grid>

							{/* Custom Hall Name Input (Only when "Other" is chosen) */}
							{effectiveIsOther && (
								<Grid item key="custom-hall-col" xs={12} sm={3.5}>
									<TextField
										label={`Custom Hall Name - ${index + 1}`}
										fullWidth
										size="small"
										required
										value={value || ""}
										onChange={(e) => {
											onChange(e.target.value);
											if (index === 0 && onHallChange) onHallChange(e.target.value);
										}}
										error={!!error}
										helperText={error?.message}
										placeholder="e.g. Celebrity Hall"
									/>
								</Grid>
							)}

							{/* Stall No */}
							<Grid item key="stall-no-col" xs={10} sm={effectiveIsOther ? 4 : 5.5}>
								<TextField
									label={`Stall No - ${index + 1}`}
									fullWidth
									size="small"
									required
									{...register(`stalls.${index}.stall_no` as const, {
										required: "Stall no is required",
									})}
									error={!!errors.stalls?.[index]?.stall_no}
									helperText={errors.stalls?.[index]?.stall_no?.message}
									placeholder="e.g. A-103"
								/>
							</Grid>

							{/* Remove Button */}
							<Grid item key="remove-btn-col" xs={2} sm={1} textAlign="center">
								<IconButton
									color="error"
									size="small"
									onClick={() => removeStall(index)}
									disabled={!canDelete}
								>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							</Grid>
						</Grid>
					);
				}}
			/>
		</Paper>
	);
};

export default StallItemRow;
